import React, { useEffect } from 'react'
import { useConfigureServers } from './hooks';
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../../store/hooks";
import { setErrorMsg } from "../../../../store/helpers/helperReducers";
import { errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { isValidDigit } from "../../../../utils/validations";
import { SelectChangeEvent } from '@mui/material';
import { Select } from '../../../ui/select';
import { Label } from '../../../ui/label';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../../../ui/tooltip';
import { InputField } from '../../../Shared/Radix/Inputs/InputField';

interface ConfigureSettingsProps {
  onValidationChange: (isValid: boolean) => void;
}

const ConfigureSettings: React.FC<ConfigureSettingsProps> = ({ onValidationChange }) => {
  const { handleOnChange, handleSelectChange, serverConfig, handleBlur, error, touched } = useConfigureServers();
  const MIN_Missed_Heartbeat_Threshold = 1;
  const MAX_Missed_Heartbeat_Threshold = 30;
  
  const errorMsg = useSelector(errorMsgSelector);
  const dispatch = useStoreDispatch();

  useEffect(() => {
    if (!serverConfig.heartbeat_freqency) {
      handleSelectChange({
        target: { name: 'heartbeat_freqency', value: Number(5) }
      } as unknown as SelectChangeEvent<string>);
    }
  }, []);

  useEffect(() => {
    const heartbeatFreq = serverConfig.heartbeat_freqency || 5; 
    const isValid = 
      heartbeatFreq > '0' && 
      serverConfig.missed_heartbeat_threshold !== undefined &&
      serverConfig.missed_heartbeat_threshold > '0' &&
      serverConfig.missed_heartbeat_threshold !== '' &&
      (parseInt(serverConfig.missed_heartbeat_threshold) >= MIN_Missed_Heartbeat_Threshold  && parseInt(serverConfig.missed_heartbeat_threshold) <= MAX_Missed_Heartbeat_Threshold);

    onValidationChange(isValid);
  }, [
    serverConfig.heartbeat_freqency,
    serverConfig.missed_heartbeat_threshold,
    onValidationChange
  ]);
  
  const handleMissedHeartbeatChange = (name: string, value: string) => {
    const trimmed = value.slice(0, 2);
    if (/^\d*$/.test(trimmed)) {
      if (isValidDigit(trimmed) && 
          (parseInt(trimmed) < MIN_Missed_Heartbeat_Threshold  || parseInt(trimmed) > MAX_Missed_Heartbeat_Threshold)){
          dispatch(setErrorMsg({ ...errorMsg, [name]: `Invalid input. Please enter a value between 1 and 30.` }));
      } 
      handleOnChange({ target: { name, value: trimmed } } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleFrequencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleSelectChange({
      target: { name: e.target.name, value: e.target.value }
    } as unknown as SelectChangeEvent<string>);
  };

  const frequencyOptions = [
    { value: "5", label: "5 seconds" },
    { value: "10", label: "10 seconds" },
    { value: "15", label: "15 seconds" },
    { value: "20", label: "20 seconds" },
    { value: "25", label: "25 seconds" },
    { value: "30", label: "30 seconds" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="heartbeat_freqency" className="text-sm font-medium">
          Heartbeat Frequency
        </Label>
        <Select
          id="heartbeat_freqency"
          name="heartbeat_freqency"
          value={String(serverConfig.heartbeat_freqency || 5)}
          options={frequencyOptions}
          onChange={handleFrequencyChange}
        />
        {touched.heartbeat_freqency && error.heartbeat_freqency && (
          <p className="text-xs text-destructive mt-0.5">{error.heartbeat_freqency}</p>
        )}
      </div>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <InputField
                label="Missed Heartbeat Threshold"
                name="missed_heartbeat_threshold"
                value={serverConfig.missed_heartbeat_threshold || ''}  
                onChange={handleMissedHeartbeatChange}
                required
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">Valid value between 1 and 30</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}

export default ConfigureSettings;
